import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId } from '../utils';

type BaseTextareaAtrributes = Pick<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  | 'id'
  | 'name'
  | 'className'
  | 'onInput'
  | 'onFocus'
  | 'value'
  | 'defaultValue'
  | 'placeholder'
  | 'disabled'
  | 'onBlur'
  | 'onChange'
  | 'autoFocus'
  | 'required'
  | 'rows'
>;

const TextareaVariants = cva(
  `focus:ring-transparent text-sm focus:outline-none transition-all rounded-lg border focus:ring-4 focus:ring-border-base p-sm`,
  {
    variants: {
      hasError: {
        true: '!border-error-text focus:ring-error-bg',
        false: '!border-border-base focus:!border-primary-base'
      },
      variant: {
        light: 'bg-background-input',
        grey: 'bg-background-input'
      },
      size: {
        sm: 'w-[240px] min-h-24',
        md: 'w-[320px] min-h-28',
        lg: 'w-[400px] min-h-32',
        full: 'w-full min-h-32'
      },
      resizable: {
        true: '!resize',
        x: '!resize-x',
        y: '!resize-y',
        false: '!resize-none'
      }
    },
    defaultVariants: {
      resizable: 'y',
      hasError: false,
      variant: 'grey',
      size: 'md'
    }
  }
);

type TextareaVariantProps = VariantProps<typeof TextareaVariants>;

export interface TextareaProps extends TextareaVariantProps, BaseTextareaAtrributes {
  testId?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => {
  const {
    value,
    defaultValue,
    onChange,
    onBlur,
    size,
    resizable,
    variant,
    hasError,
    placeholder,
    className,
    testId,
    ...rest
  } = props;

  // Support both controlled and uncontrolled usage
  const isControlled = value !== undefined;
  const textareaProps = isControlled
    ? { value: value ?? '' }
    : { defaultValue: defaultValue ?? '' };

  return (
    <textarea
      {...rest}
      {...textareaProps}
      ref={ref}
      data-testid={getTestId({ testId, id: rest.id, name: rest.name })}
      aria-invalid={hasError || undefined}
      aria-required={rest.required || undefined}
      placeholder={placeholder ?? 'Enter a description'}
      onChange={onChange}
      onBlur={onBlur}
      className={cn(TextareaVariants({ hasError, resizable, size, variant }), className)}
    />
  );
});

Textarea.displayName = 'Textarea';
